import { BookingSlot } from "@/templates/TemplateInterface";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { formatPrice as formatPriceUtil } from "@/utils/currency";

interface BookingSlotCardProps {
  slot: BookingSlot;
  variant?: "default" | "compact";
  currency?: string;
  themeColors?: {
    primaryColor?: string;
    primary?: string;
    secondaryColor?: string;
    secondary?: string;
  };
  onSlotClick?: (slot: BookingSlot) => void;
}

const BOOKING_TYPE_LABELS: Record<string, string> = {
  car_hire: "Car Hire",
  apartment: "Apartment",
  hotel: "Hotel",
  service: "Service",
  other: "Booking",
};

export function BookingSlotCard({
  slot,
  variant = "default",
  currency = "NGN",
  themeColors = { primaryColor: "#3b82f6", secondaryColor: "#10b981" },
  onSlotClick,
}: BookingSlotCardProps) {
  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency });

  const primaryColor =
    themeColors.primaryColor || themeColors.primary || "#3b82f6";

  const handleSlotClick = () => {
    if (onSlotClick) {
      onSlotClick(slot);
    }
  };

  const typeLabel = BOOKING_TYPE_LABELS[slot.bookingType] || slot.bookingType;

  if (variant === "compact") {
    return (
      <div
        className="cursor-pointer overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition-shadow hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.2)]"
        onClick={handleSlotClick}
      >
        <div className="p-4">
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3 className="flex-1 truncate text-sm font-semibold sm:text-base">
              {slot.name}
            </h3>
            <span
              className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{
                backgroundColor: `${primaryColor}18`,
                color: primaryColor,
              }}
            >
              {typeLabel}
            </span>
          </div>
          {slot.description && (
            <p className="mb-2 line-clamp-2 text-xs text-black/45">
              {slot.description}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between">
            <div>
              <div
                className="text-sm font-semibold sm:text-base"
                style={{ color: primaryColor }}
              >
                {formatPrice(slot.price)}
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-black/40">
                <Calendar className="h-3 w-3" />
                {slot.duration}
              </div>
            </div>
            <Button 
            size="sm" 
            className="rounded-full text-xs text-white hover:!text-white hover:opacity-90 sm:text-sm"
            style={{ backgroundColor: primaryColor }}
            onClick={(e) => {
              e.stopPropagation();
              handleSlotClick();
            }}
          >
            Book Now
          </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition-shadow hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.2)]"
      onClick={handleSlotClick}
    >
      <div className="relative">
        {slot.images?.[0] ? (
          <div className="aspect-square overflow-hidden bg-black/[0.03]">
            <img
              src={slot.images[0].url}
              alt={slot.images[0].alt || slot.name}
              className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
            />
          </div>
        ) : (
          <div
            className="flex aspect-square items-center justify-center"
            style={{ backgroundColor: `${primaryColor}12` }}
          >
            <Calendar className="h-12 w-12" style={{ color: `${primaryColor}55` }} />
          </div>
        )}

        <div
          className="absolute right-2.5 top-2.5 rounded-full px-2.5 py-1 text-[10px] font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {typeLabel}
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="mb-1 truncate text-sm font-semibold sm:text-base">
          {slot.name}
        </h3>
        {slot.description && (
          <p className="mb-2 line-clamp-2 text-xs text-black/45">
            {slot.description}
          </p>
        )}

        <div className="mt-2 sm:mt-3">
          <div className="mb-2 flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-black/35" />
            <span className="text-xs text-black/50">{slot.duration}</span>
            {slot.capacity > 1 && (
              <>
                <span className="text-black/25">·</span>
                <span className="text-xs text-black/50">
                  {slot.capacity} people
                </span>
              </>
            )}
          </div>

          <div>
            <div
              className="text-sm font-semibold sm:text-base"
              style={{ color: primaryColor }}
            >
              {formatPrice(slot.price)}
            </div>
            {slot.compareAtPrice && slot.compareAtPrice > slot.price && (
              <div className="text-xs line-through text-black/35">
                {formatPrice(slot.compareAtPrice)}
              </div>
            )}
          </div>

          <Button
            size="sm"
            className="mt-3 w-full rounded-full text-xs font-semibold text-white hover:!text-white hover:opacity-90 sm:text-sm"
            style={{ backgroundColor: primaryColor }}
            onClick={(e) => {
              e.stopPropagation();
              handleSlotClick();
            }}
          >
            <Calendar className="mr-1.5 h-3.5 w-3.5" />
            Book Now
          </Button>
        </div>
      </div>
    </div>
  );
}
